import React, { useEffect, useState } from "react";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer, Legend, LabelList } from "recharts";
import axios from "axios";
import { useAppContext } from "../context/AppContext";
import "../styles/PieChartComponent.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Modern gradient colors with semantic meaning
const COLORS = ["#10b981", "#ef4444", "#f59e0b"]; // Green, Red, Amber
const GRADIENT_COLORS = [
  { start: "#10b981", end: "#059669", shadow: "rgba(16, 185, 129, 0.3)" }, // Green gradient
  { start: "#ef4444", end: "#dc2626", shadow: "rgba(239, 68, 68, 0.3)" }, // Red gradient  
  { start: "#f59e0b", end: "#d97706", shadow: "rgba(245, 158, 11, 0.3)" }  // Amber gradient
];

const PieChartComponent = ({ refreshKey = 0 }) => {
  const { user } = useAppContext();
  const [data, setData] = useState([]);
  const [totalGuests, setTotalGuests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    axios.get(`${BASE_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${user.token}` }
      })
      .then((res) => {
        const chartData = [
          { 
            name: "מגיעים", 
            nameEn: "Arriving",
            value: res.data.totalGuestsComing,
            percentage: 0
          },
          { 
            name: "לא מגיעים", 
            nameEn: "Not Coming",
            value: res.data.countDontArrive,
            percentage: 0
          },
          { 
            name: "לא ענו", 
            nameEn: "No Response",
            value: res.data.countDontKnow,
            percentage: 0
          },
        ];
        
        const total = chartData.reduce((sum, item) => sum + item.value, 0);
        const dataWithPercentages = chartData.map(item => ({
          ...item,
          percentage: total > 0 ? Math.round((item.value / total) * 100) : 0
        }));
        
        setData(dataWithPercentages);
        setTotalGuests(total);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching stats", err);
        setError("שגיאה בטעינת הנתונים");
        setLoading(false);
      });
  }, [user, refreshKey]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <div className="tooltip-header">
            <div 
              className="color-indicator" 
              style={{ backgroundColor: payload[0].color }}
            ></div>
            <span className="tooltip-label">{data.name}</span>
          </div>
          <div className="tooltip-content">
            <span className="tooltip-value">{data.value} אורחים</span>
            <span className="tooltip-percentage">({data.percentage}%)</span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom label function with enhanced visibility
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, percentage, name }) => {
    if (percentage < 5) return null; // Don't show label for very small slices
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g className="pie-label-group">
        <circle 
          cx={x} 
          cy={y} 
          r="28" 
          fill="rgba(255, 255, 255, 0.95)" 
          stroke="rgba(0, 0, 0, 0.1)"
          strokeWidth="1"
          filter="drop-shadow(0px 2px 8px rgba(0,0,0,0.15))"
        />
        <text 
          x={x} 
          y={y - 6} 
          fill="#1e293b" 
          textAnchor="middle" 
          dominantBaseline="central"
          fontSize="14"
          fontWeight="800"
          style={{ userSelect: 'none' }}
        >
          {`${percentage}%`}
        </text>
        <text 
          x={x} 
          y={y + 8} 
          fill="#64748b" 
          textAnchor="middle" 
          dominantBaseline="central"
          fontSize="11"
          fontWeight="700"
          style={{ userSelect: 'none' }}
        >
          {value}
        </text>
      </g>
    );
  };

  // Custom legend with enhanced interaction
  const CustomLegend = ({ payload }) => {
  
    return (
      <div className="chart-legend">
        {payload.map((entry, index) => {
            if (entry.value === "מגיעים"){
      entry.color = "#10b981"; // Green for arriving
      entry.background = "linear-gradient(135deg, #f0fdf4, #ecfdf5)"; // Almost white green gradient background
    }
    else if (entry.value === "לא מגיעים") {
      entry.color = "#ef4444"; // Red for not coming
      entry.background = "linear-gradient(135deg, #fef2f2, #fef1f1)"; // Almost white red gradient background

    }
    else {
      entry.color = "#f59e0b"; // Amber for no response
      entry.background = "linear-gradient(135deg, #fffbeb, #fefce8)"; // Almost white amber gradient background
    }
          return(
          <div key={index} className="legend-item" data-category={entry.payload.nameEn}>
            <div className="legend-content">
              <span className="legend-label">{entry.payload.name}</span>
              <div className="legend-stats">
                <span className="legend-value">{entry.payload.value}</span>
                <span style={{color: entry.color, background: entry.background, borderColor: entry.color}} className="legend-percentage">{entry.payload.percentage}%</span>
              </div>
            </div>
          </div>
        )})}
      </div>
    );
  };

  return (
    <div className="pie-chart-wrapper">
      <div className="chart-header">
        <div className="total-guests">
          <span className="total-number">{totalGuests}</span>
          <span className="total-label">סה"כ אורחים</span>
        </div>
      </div>
      
      {loading ? (
        <div className="chart-loading">
          טוען נתונים...
        </div>
      ) : error ? (
        <div className="chart-error">
          <span>❌ {error}</span>
          <button onClick={() => window.location.reload()}>נסה שוב</button>
        </div>
      ) : (
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <defs>
                {GRADIENT_COLORS.map((gradient, index) => (
                  <React.Fragment key={index}>
                    <linearGradient 
                      id={`gradient${index}`} 
                      x1="0%" 
                      y1="0%" 
                      x2="100%" 
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={gradient.start} />
                      <stop offset="100%" stopColor={gradient.end} />
                    </linearGradient>
                    <filter id={`shadow${index}`}>
                      <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor={gradient.shadow} />
                    </filter>
                  </React.Fragment>
                ))}
              </defs>
              
              <Pie 
                data={data} 
                cx="50%" 
                cy="50%" 
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                innerRadius={55}
                fill="#8884d8" 
                dataKey="value"
                stroke="rgba(255, 255, 255, 0.9)"
                strokeWidth={3}
                startAngle={90}
                endAngle={450}
                animationBegin={200}
                animationDuration={800}
                onClick={null}
                onMouseEnter={null}
                onMouseLeave={null}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={`url(#gradient${index})`}
                    filter={`url(#shadow${index})`}
                    style={{
                      cursor: 'default',
                      pointerEvents: 'none'
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PieChartComponent;
